import React, { useMemo, useState } from "react";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "./ui/accordion";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from "@tanstack/react-table";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "./ui/table";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import useFirebaseUserData from "../hooks/useFirebaseUserData";
import useFirebaseUsers from "../hooks/useFirebaseUsers";
import DatabaseIntegrity from "./DatabaseIntegrity";

import { useAuth, superUserUID } from "../helpers/Auth";

const SuperUserPage = () => {
  const { currentUser, fakeUID, setFakeUID } = useAuth();
  const userData = useFirebaseUserData();
  const users = useFirebaseUsers();
  const [sorting, setSorting] = useState([{ id: "name", desc: false }]);

  const user_array = useMemo(() => {
    if (!users) return [];
    return Object.keys(users).map((key) => ({
      ...users[key],
      id: key,
      num_kilns: userData?.[key]?.kilns ? Object.keys(userData[key].kilns).length : 0,
      num_projects: userData?.[key]?.projects ? Object.keys(userData[key].projects).length : 0,
    }));
  }, [users, userData]);

  const totals = useMemo(() => {
    return user_array.reduce(
      (acc, user) => ({
        users: acc.users + 1,
        kilns: acc.kilns + (user.num_kilns || 0),
        projects: acc.projects + (user.num_projects || 0),
      }),
      { users: 0, projects: 0, kilns: 0 }
    );
  }, [user_array]);

  const columns = useMemo(
    () => [
      {
        id: "photo",
        header: "Photo",
        accessorKey: "photoURL",
        enableSorting: false,
        cell: ({ row }: { row: any }) => (
          <img className="inline-block h-7 w-7 rounded-full" src={row.original.photoURL} alt="" />
        ),
      },
      {
        header: "UID",
        accessorKey: "uid",
      },
      {
        header: "Name",
        accessorKey: "name",
      },
      {
        header: "Email",
        accessorKey: "email",
      },
      {
        header: "# Kilns",
        accessorKey: "num_kilns",
      },
      {
        header: "# Projects",
        accessorKey: "num_projects",
      },
    ],
    []
  );

  const table = useReactTable({
    data: user_array,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  if (!currentUser || currentUser.uid !== superUserUID) {
    return <div>Access Denied</div>;
  }
  return (
    <div>
      <h1 className="text-3xl font-bold my-4">Super User</h1>
      {fakeUID && (
        <div>
          You are current impersonating a user.
          <div onClick={() => setFakeUID(undefined)}>STOP IMPERSONATING!</div>
        </div>
      )}
      <Accordion type="single" collapsible>
        <AccordionItem value="statistics">
          <AccordionTrigger>Statistics</AccordionTrigger>
          <AccordionContent>
            Users: {totals.users}
            <br />
            Kilns: {totals.kilns}
            <br />
            Projects: {totals.projects}
            <br />
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="users">
          <AccordionTrigger>Show Users</AccordionTrigger>
          <AccordionContent>
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead
                        key={header.id}
                        className={header.column.getCanSort() ? "cursor-pointer select-none" : ""}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getCanSort() && (
                          <>
                            {header.column.getIsSorted() === "asc" && (
                              <ArrowUp className="inline-block h-3 w-3 ml-1" />
                            )}
                            {header.column.getIsSorted() === "desc" && (
                              <ArrowDown className="inline-block h-3 w-3 ml-1" />
                            )}
                            {!header.column.getIsSorted() && (
                              <ArrowUpDown className="inline-block h-3 w-3 ml-1 opacity-30" />
                            )}
                          </>
                        )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    className="cursor-pointer"
                    onClick={() => {
                      setFakeUID(row.original.id);
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </AccordionContent>
        </AccordionItem>
        <AccordionItem value="integrity">
          <AccordionTrigger>Show Database Integrity</AccordionTrigger>
          <AccordionContent>
            <DatabaseIntegrity />
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

export default SuperUserPage;
